import { useMemo, useState } from 'react';

import { Button } from '@components/button';
import { CollapseCard } from '@components/card/collapse-card';
import { createForm, useCreateFormStore } from '@components/form';
import { IconAddPlus } from '@components/icons/add-plus';
import { IconFieldCustom } from '@components/icons/field-custom';
import { IconInput } from '@components/icons/input';
import { Separator } from '@components/separator';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { sanitizeJSON } from '@utils/collections';
import { pxToRem } from '@utils/converters';

import { useUpdateIntegrationEndpoint } from 'api/integrations/hooks';
import { IntegrationEndpoint, IntegrationEndpointVariable } from 'api/integrations/requests';

import {
  FieldTable,
  generateFieldName,
  getFormEndpointVariables,
  getVariableCount,
} from './common';
import { CUSTOM_FIELD_PREFIX } from './common';
import { getFieldIds } from './endpoint';
import { EditCustomResponseValueSidebar } from './sidebars/edit-custom-response-value';

type Data = {};

const Form = createForm<Data>();

type SidebarEditingState = {
  isOpen: boolean;
  value: IntegrationEndpointVariable | null;
  path: string[];
  scopedData: object | null;
};

const visibleColumns = {
  name: true,
  type: true,
  label: true,
  example: false,
  default: false,
  visible: false,
  format: true,
};

export const ResponseValueCard = ({
  endpoint,
  defaultCollapsed,
  onVisibilityToggle,
}: {
  endpoint: IntegrationEndpoint<string>;
  defaultCollapsed: boolean;
  onVisibilityToggle(isCollapsed: boolean): void;
}) => {
  const { t } = useScopedTranslate(locale);
  const formStore = useCreateFormStore();
  const formIsDirty = formStore.useIsDirty();

  const [modifiedCustomValueIds, setModifiedCustomValueIds] = useState<Set<string>>(new Set());
  const [removedFieldIds, setRemovedFieldIds] = useState<Set<string>>(new Set());
  const [localResponseValues, setLocalResponseValues] = useState<IntegrationEndpointVariable[]>(
    endpoint.responseValues || []
  );
  const [sidebarEditing, setSidebarEditing] = useState<SidebarEditingState>({
    isOpen: false,
    value: null,
    path: [],
    scopedData: null,
  });

  const updateIntegrationEndpointMutation = useUpdateIntegrationEndpoint();

  const hasUnsavedChanges =
    formIsDirty || modifiedCustomValueIds.size > 0 || removedFieldIds.size > 0;

  const handleUpdateEndpoint = async () => {
    const endpointResponseValues = getFormEndpointVariables({
      formStore,
      endpointVariables: localResponseValues, // Use local state which includes structural changes
      visibleColumns,
    });

    await updateIntegrationEndpointMutation.mutateAsync({
      shortId: endpoint.shortId,
      data: {
        responseValues: endpointResponseValues,
      },
    });

    // Mark form as clean (treat current values as new baseline)
    // and clear modified tracking after successful save
    formStore.actions.markAsClean();
    setModifiedCustomValueIds(new Set());
    setRemovedFieldIds(new Set());
    // Update local state with the merged values to keep it in sync with backend
    setLocalResponseValues(endpointResponseValues);
  };

  const handleRemoveField = (name: string) => {
    const newVariables = removeFromVariables(localResponseValues, name);
    setLocalResponseValues(newVariables);

    const fieldExistsInOriginal = (fieldName: string): boolean => {
      const checkInArray = (variables: IntegrationEndpointVariable[]): boolean => {
        return variables.some((v) => {
          if (v.name === fieldName) return true;
          if (v.template) return checkInArray(v.template);
          return false;
        });
      };
      return checkInArray(endpoint.responseValues || []);
    };

    // Determine if this was a newly added custom value or an existing field
    const wasNewlyAdded = modifiedCustomValueIds.has(name) && !fieldExistsInOriginal(name);

    if (wasNewlyAdded) {
      // If this was a modified custom value that hasn't been saved yet, remove from tracking
      setModifiedCustomValueIds((prev) => {
        const next = new Set(prev);
        next.delete(name);
        return next;
      });
    } else {
      // Field existed in the original, track as removed
      setRemovedFieldIds((prev) => new Set(prev).add(name));
    }
  };

  const handleOpenCustomValueSidebar = (path: string[], scopedData: any, existingValue?: any) => {
    // If editing an existing value, merge with current form state to show latest unsaved changes
    let valueToEdit;
    if (existingValue) {
      // Reconstruct the flattened field name used in the form
      // For nested fields, the form uses: path.join('__') + '__' + fieldName
      // e.g., 'users__devices___custom_123'
      const flattenedName =
        path.length > 0 ? `${path.join('__')}__${existingValue.name}` : existingValue.name;

      const { labelId, typeId, jqQueryId } = getFieldIds(flattenedName);
      const currentLabel = formStore.getState().values[labelId];
      const currentType = formStore.getState().values[typeId];
      const currentQuery = formStore.getState().values[jqQueryId];

      valueToEdit = {
        ...existingValue,
        label: currentLabel ?? existingValue.label,
        type: currentType ?? existingValue.type,
        jqQuery: currentQuery ?? existingValue.jqQuery,
      };
    } else {
      // Creating new custom value
      valueToEdit = {
        name: generateFieldName(),
        label: '',
        jqQuery: '',
        type: 'text',
      };
    }

    setSidebarEditing({
      isOpen: true,
      path,
      scopedData,
      value: valueToEdit,
    });
  };

  const handleSaveCustomValue = (savedValue: IntegrationEndpointVariable) => {
    const flattenedName =
      sidebarEditing.path.length > 0
        ? `${sidebarEditing.path.join('__')}__${savedValue.name}`
        : savedValue.name;

    const updatedResponseValues = updateValueAtPath(
      localResponseValues,
      sidebarEditing.path,
      savedValue
    );
    setLocalResponseValues(updatedResponseValues);
    setModifiedCustomValueIds((prev) => new Set(prev).add(flattenedName));
    formStore.actions.setValue(`${flattenedName}---label`, savedValue.label);
    formStore.actions.setValue(`${flattenedName}---type`, savedValue.type);
    formStore.actions.setValue(`${flattenedName}---jqQuery`, savedValue.jqQuery);
  };

  const formActionPortalId = `${endpoint.shortId}-response-values-form-actions-portal`;

  const customs = localResponseValues.filter((val) => {
    return val.name.startsWith(CUSTOM_FIELD_PREFIX);
  });
  const withoutCustoms = localResponseValues.filter((val) => {
    return !val.name.startsWith(CUSTOM_FIELD_PREFIX);
  });

  const exampleResponseData = useMemo(() => {
    return sanitizeJSON(endpoint.exampleResponseData);
  }, [endpoint.exampleResponseData]);

  return (
    <div>
      <CollapseCard
        key={endpoint.shortId}
        keepCollapsedInDom
        defaultCollapsed={defaultCollapsed}
        onVisibilityToggle={onVisibilityToggle}
        spaceX={4}
        spaceY={2}
        variant="gray"
        renderCollapse={() => (
          <Form store={formStore} onSubmit={handleUpdateEndpoint} space={4}>
            {!localResponseValues || Object.keys(localResponseValues).length === 0 ? (
              <Stack>
                <Text font="regular14" color="textSecondary" text={t('noResponseValues')} />
                <Text font="regular14" color="textSecondary" text={t('afterSuccessfulResponse')} />
              </Stack>
            ) : (
              <Stack space={4}>
                <Stack space={2}>
                  <FieldTable
                    endpointId={endpoint.shortId}
                    form={Form}
                    endpointVariables={withoutCustoms}
                    visibleColumns={visibleColumns}
                    handleRemoveField={handleRemoveField}
                    showArrayType
                    customValueOptions={{
                      exampleData: exampleResponseData,
                      onOpenCustomValueSidebar: handleOpenCustomValueSidebar,
                    }}
                  />
                </Stack>

                <Separator />
                <Stack orientation="horizontal" justify="space-between" align="center">
                  <Stack orientation="horizontal" align="center" space={2}>
                    <IconFieldCustom color="elementPurple50" />
                    <Text text={t('customResponseValues')} font="semibold14" />
                    <Text text="•" color="textHelper" />
                    <Text text={getVariableCount(customs)} font="semibold14" />
                  </Stack>
                  {exampleResponseData && typeof exampleResponseData === 'object' && (
                    <Stack orientation="horizontal" space={4} align="center">
                      <Button
                        text={t('addRootCustomValue')}
                        variant="primary"
                        iconLeft={<IconAddPlus />}
                        onClick={() => handleOpenCustomValueSidebar([], exampleResponseData)}
                      />
                    </Stack>
                  )}
                </Stack>
                {customs.length > 0 ? (
                  <FieldTable
                    fieldColumnLabel="./jq query"
                    endpointId={endpoint.shortId}
                    form={Form}
                    endpointVariables={customs}
                    visibleColumns={visibleColumns}
                    handleRemoveField={handleRemoveField}
                    showArrayType
                    customValueOptions={{
                      exampleData: exampleResponseData,
                      onOpenCustomValueSidebar: handleOpenCustomValueSidebar,
                    }}
                  />
                ) : (
                  <Text
                    style={{ marginBottom: pxToRem(16) }}
                    font="regular14"
                    color="textSecondary"
                    text={t('noCustom')}
                  />
                )}
              </Stack>
            )}
            {hasUnsavedChanges && (
              <Form.Actions portal={formActionPortalId}>
                <Stack orientation="horizontal" space={2} align="center">
                  <Form.Reset
                    stopPropagation
                    text={t('core.cancel')}
                    variant="secondary"
                    onClick={() => {
                      formStore.actions.resetStore();
                      setModifiedCustomValueIds(new Set());
                      setRemovedFieldIds(new Set());
                      setLocalResponseValues(endpoint.responseValues || []);
                    }}
                  />
                  <Form.Submit
                    stopPropagation
                    isDisabled={updateIntegrationEndpointMutation.isLoading}
                    text={t('core.save')}
                    variant="primary-blue"
                  />
                </Stack>
              </Form.Actions>
            )}
          </Form>
        )}
      >
        <Stack orientation="horizontal" justify="space-between" align="center">
          <Stack orientation="horizontal" align="center" space={2}>
            <IconInput color="elementPurple50" />
            <Text text={t('responseValues')} font="semibold14" />
            <Text text="•" color="textHelper" />
            <Text text={getVariableCount(localResponseValues)} font="semibold14" />
          </Stack>
          <Stack orientation="horizontal" space={4} align="center">
            <div id={formActionPortalId} />
          </Stack>
        </Stack>
      </CollapseCard>

      {exampleResponseData && typeof exampleResponseData === 'object' && (
        <EditCustomResponseValueSidebar
          key={String(sidebarEditing.path) + String(sidebarEditing.isOpen)}
          value={sidebarEditing.value}
          exampleData={sidebarEditing.scopedData}
          isOpen={sidebarEditing.isOpen}
          onClose={() => {
            setSidebarEditing({
              isOpen: false,
              value: null,
              path: [],
              scopedData: null,
            });
          }}
          onSave={handleSaveCustomValue}
        />
      )}
    </div>
  );
};

const updateValueAtPath = (
  variables: IntegrationEndpointVariable[],
  path: string[],
  value: IntegrationEndpointVariable
): IntegrationEndpointVariable[] => {
  // If path is empty, we're at the root level
  if (path.length === 0) {
    const existingIndex = variables.findIndex((v) => v.name === value.name);
    if (existingIndex >= 0) {
      // Update existing value
      const updated = [...variables];
      updated[existingIndex] = value;
      return updated;
    } else {
      // Add new value
      return [...variables, value];
    }
  }

  // Navigate to the correct array's template
  const [currentSegment, ...remainingPath] = path;
  return variables.map((variable) => {
    if (variable.name === currentSegment && variable.type === 'array') {
      // Found the array, update its template
      return {
        ...variable,
        template: updateValueAtPath(variable.template || [], remainingPath, value),
      };
    }
    return variable;
  });
};

const removeFromVariables = (
  variables: IntegrationEndpointVariable[],
  name: string
): IntegrationEndpointVariable[] => {
  return variables
    .map((variable) => {
      if (variable.type === 'array' && variable.template) {
        // Recursively process template
        const newTemplate = removeFromVariables(variable.template, name);
        return { ...variable, template: newTemplate };
      }
      return variable;
    })
    .filter((variable) => {
      // Remove if name matches or if array has empty template
      return (
        variable.name !== name && !(variable.type === 'array' && variable.template?.length === 0)
      );
    });
};

const locale = {
  en: {
    responseValues: 'Response values',
    customResponseValues: 'Custom response values',
    noResponseValues:
      'No values received, please add your test request values in the API call and click test',
    afterSuccessfulResponse:
      'Once you receive a successful response the mappable values will appear here',
    addRootCustomValue: 'Add custom value',
    noCustom:
      'No custom values. Click "Add custom value" to create computed values using ./jq queries.',
  },
};
